import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { Spinner } from './components/Spinner';
import { DocsPage } from './pages/DocsPage';

/**
 * Routing, split along one line: does this route need Firebase or not?
 *
 * `lib/firebase.ts` initialises at module scope and getAuth() throws
 * synchronously when the config is absent or wrong. A static import of anything
 * that reaches it therefore takes down the entire bundle before React mounts —
 * a blank page, not a degraded one.
 *
 * Docs are public and need no identity, so they are imported directly and every
 * authenticated surface is loaded lazily behind it. That keeps Firebase out of
 * the chunk /docs downloads, so a misconfigured deployment still serves the
 * documentation that explains how to configure it.
 */

// Authenticated surfaces. Lazy so their imports — and Firebase with them — stay
// out of the initial chunk.
const AuthProvider = lazy(() =>
  import('./contexts/AuthContext').then((m) => ({ default: m.AuthProvider })),
);
const AuthGuard = lazy(() =>
  import('./components/AuthGuard').then((m) => ({ default: m.AuthGuard })),
);
const AppShell = lazy(() =>
  import('./components/layout/AppShell').then((m) => ({ default: m.AppShell })),
);
const AgencyDashboardPage = lazy(() =>
  import('./pages/AgencyDashboardPage').then((m) => ({ default: m.AgencyDashboardPage })),
);
const BillingPage = lazy(() =>
  import('./pages/BillingPage').then((m) => ({ default: m.BillingPage })),
);
const ConnectorsPage = lazy(() =>
  import('./pages/ConnectorsPage').then((m) => ({ default: m.ConnectorsPage })),
);
const MeetingPage = lazy(() =>
  import('./pages/MeetingPage').then((m) => ({ default: m.MeetingPage })),
);
const MeetingSummaryPage = lazy(() =>
  import('./pages/MeetingSummaryPage').then((m) => ({ default: m.MeetingSummaryPage })),
);
const PortalPage = lazy(() =>
  import('./pages/PortalPage').then((m) => ({ default: m.PortalPage })),
);
const PortalSignInPage = lazy(() =>
  import('./pages/PortalSignInPage').then((m) => ({ default: m.PortalSignInPage })),
);
const PricingPage = lazy(() =>
  import('./pages/PricingPage').then((m) => ({ default: m.PricingPage })),
);
const ProfileEditorPage = lazy(() =>
  import('./pages/ProfileEditorPage').then((m) => ({ default: m.ProfileEditorPage })),
);
const ProfileRouterPage = lazy(() =>
  import('./pages/ProfileRouterPage').then((m) => ({ default: m.ProfileRouterPage })),
);
const SeatsPage = lazy(() =>
  import('./pages/SeatsPage').then((m) => ({ default: m.SeatsPage })),
);
const SignInPage = lazy(() =>
  import('./pages/SignInPage').then((m) => ({ default: m.SignInPage })),
);
const WorkspacePage = lazy(() =>
  import('./pages/WorkspacePage').then((m) => ({ default: m.WorkspacePage })),
);

/**
 * Everything that needs an identity sits under this layout.
 *
 * AuthProvider is mounted here rather than around the whole tree so that a
 * route outside it — the docs — never constructs it.
 */
function AuthedArea() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* No identity, no Firebase. Readable on a deployment that has not
              been configured yet, which is exactly when someone needs it. */}
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/:slug" element={<DocsPage />} />

          <Route element={<AuthedArea />}>
            {/* Public, but still inside the provider: these read auth state. */}
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/portal/signin" element={<PortalSignInPage />} />
            <Route path="/portal" element={<PortalPage />} />
            <Route path="/pricing" element={<PricingPage />} />

            {/* Protected */}
            <Route element={<AuthGuard />}>
              <Route element={<AppShell />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<WorkspacePage />} />
                <Route path="meetings/:meetingId" element={<MeetingPage />} />
                <Route path="meetings/:meetingId/summary" element={<MeetingSummaryPage />} />
                <Route path="profile" element={<ProfileRouterPage />} />
                <Route path="profile/:id" element={<ProfileEditorPage />} />
                <Route path="connectors" element={<ConnectorsPage />} />
                <Route path="seats" element={<SeatsPage />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="agency" element={<AgencyDashboardPage />} />
              </Route>
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
